import React, { useEffect, useState, useMemo, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { dentroDaMeta } from '../../lib/metas';
import { useAuth } from '../../hooks/useAuth';
import { Sale, User } from '../../types';
import { startOfMonth, endOfMonth, isSameDay, format, subMonths } from 'date-fns';
import confetti from 'canvas-confetti';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, Star, Flame, Target, Users, Award, ArrowUp, ArrowDown, Zap, TrendingUp, CheckCircle2, Volume2, VolumeX, Crown, Sparkles } from 'lucide-react';
import { useUsersData } from '../../hooks/useUsersData';
import { useGoalsData } from '../../hooks/useGoalsData';
import { NIVEIS_ACM, calcularNivel, ehVendaAcm } from '../../../supabase/functions/_shared/niveisAcm';

interface LeaderboardUser extends User {
  monthlySales: number;
  monthlyRevenue: number;
  dailySales: number;
  dailyRevenue: number;
  rank: number;
  previousRank?: number;
  streak: number;
  sparkline: number[];
  maxSingleSale: number;
  levelIndex: number;
  levelBarToBeat: number;
}

// Trilha de níveis do Método ACM e comissão de cada nível: regra compartilhada com a folha de pagamento
// (supabase/functions/_shared/niveisAcm.ts).
const ACM_LEVELS = NIVEIS_ACM;

const monthKey = (d: Date) => format(d, 'yyyy-MM');

const computeLevelStatus = calcularNivel;

interface LeaderboardBadges {
  dailyLeaderId?: string;
  topSaleId?: string;
  comebackId?: string;
  consistentId?: string;
}

const BIG_SALE_THRESHOLD = 10000; // dispara o banner de comemoração em tela cheia

// Silhueta de escudo heráldico (heater shield) usada pelas três artes de
// escudo (dourado, ferro, luz azul) — viewBox 0 0 100 120.
const SHIELD_D = 'M50 3 C25 3 8 12 8 12 L8 55 C8 88 26 108 50 119 C74 108 92 88 92 55 L92 12 C92 12 75 3 50 3 Z';

// Recorte em forma de escudo aplicado à foto do vendedor, montada como um
// "brasão" sobre a arte do escudo grande.
const SHIELD_CLIP = 'polygon(50% 0%, 93% 14%, 93% 60%, 50% 100%, 7% 60%, 7% 14%)';

const ShieldArt: React.FC<{ tier: 'gold' | 'iron' | 'bronze' }> = ({ tier }) => {
  if (tier === 'gold') {
    return (
      <svg viewBox="0 0 100 120" className="w-full h-full" style={{ filter: 'drop-shadow(0 0 25px rgba(250,204,21,0.85)) drop-shadow(0 8px 18px rgba(0,0,0,0.55))' }}>
        <defs>
          <linearGradient id="shieldGoldFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#fff7cc" />
            <stop offset="45%" stopColor="#fbbf24" />
            <stop offset="100%" stopColor="#92400e" />
          </linearGradient>
          <radialGradient id="shieldGoldGem" cx="35%" cy="35%" r="70%">
            <stop offset="0%" stopColor="#fff7ed" />
            <stop offset="60%" stopColor="#f97316" />
            <stop offset="100%" stopColor="#9a3412" />
          </radialGradient>
        </defs>
        <path d={SHIELD_D} fill="url(#shieldGoldFill)" stroke="#fef3c7" strokeWidth="1.5" />
        <path d={SHIELD_D} fill="none" stroke="#fde68a" strokeWidth="0.6" opacity="0.6" transform="translate(7.5 9) scale(0.85)" />
        <circle cx="50" cy="44" r="10" fill="url(#shieldGoldGem)" stroke="#fef3c7" strokeWidth="1" />
      </svg>
    );
  }
  if (tier === 'iron') {
    const rivets: [number, number][] = [[16, 24], [84, 24], [10, 52], [90, 52], [24, 92], [76, 92]];
    return (
      <svg viewBox="0 0 100 120" className="w-full h-full" style={{ filter: 'drop-shadow(0 0 18px rgba(148,163,184,0.7)) drop-shadow(0 8px 18px rgba(0,0,0,0.7))' }}>
        <defs>
          <linearGradient id="shieldIronFill" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#94a3b8" />
            <stop offset="50%" stopColor="#334155" />
            <stop offset="100%" stopColor="#020617" />
          </linearGradient>
          <radialGradient id="shieldIronBoss" cx="35%" cy="35%" r="70%">
            <stop offset="0%" stopColor="#cbd5e1" />
            <stop offset="60%" stopColor="#334155" />
            <stop offset="100%" stopColor="#020617" />
          </radialGradient>
        </defs>
        <path d={SHIELD_D} fill="url(#shieldIronFill)" stroke="#64748b" strokeWidth="1.5" />
        {rivets.map(([x, y], i) => <circle key={i} cx={x} cy={y} r="2.6" fill="#020617" stroke="#64748b" strokeWidth="0.5" />)}
        <circle cx="50" cy="47" r="11" fill="url(#shieldIronBoss)" stroke="#0f172a" strokeWidth="1.2" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 100 120" className="w-full h-full" style={{ filter: 'drop-shadow(0 0 22px rgba(217,119,6,0.8)) drop-shadow(0 8px 18px rgba(0,0,0,0.55))' }}>
      <defs>
        <linearGradient id="shieldBronzeFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#fde9c8" />
          <stop offset="45%" stopColor="#d97706" />
          <stop offset="100%" stopColor="#5c2d0c" />
        </linearGradient>
        <radialGradient id="shieldBronzeGem" cx="35%" cy="35%" r="70%">
          <stop offset="0%" stopColor="#fff1e0" />
          <stop offset="60%" stopColor="#b45309" />
          <stop offset="100%" stopColor="#451a03" />
        </radialGradient>
      </defs>
      <path d={SHIELD_D} fill="url(#shieldBronzeFill)" stroke="#fde9c8" strokeWidth="1.5" />
      <path d={SHIELD_D} fill="none" stroke="#fbbf24" strokeWidth="0.6" opacity="0.5" transform="translate(7.5 9) scale(0.85)" />
      <circle cx="50" cy="44" r="9" fill="url(#shieldBronzeGem)" stroke="#fde9c8" strokeWidth="1" />
    </svg>
  );
};

// Wisps de energia subindo da base do escudo, tipo aura de ki — várias
// "chamas" borradas de cor da aura que sobem e somem, em loop escalonado.
// `intensity` (0-1) controla quantos wisps aparecem e o pico de opacidade —
// a aura fica mais "foda" quanto mais alta a colocação.
const KiWisps: React.FC<{ color: string; width: number; intensity: number }> = ({ color, width, intensity }) => {
  const allOffsets = [-0.38, -0.22, -0.07, 0.07, 0.22, 0.38];
  const count = Math.max(2, Math.round(allOffsets.length * intensity));
  const offsets = allOffsets.filter((_, i) => i < count || allOffsets.length - i <= Math.ceil(count / 2)).slice(0, count);
  const peakOpacity = 0.75 + intensity * 0.25;
  return (
    <>
      {offsets.map((frac, i) => (
        <motion.div
          key={i}
          className="absolute bottom-4 rounded-full blur-md pointer-events-none z-[17]"
          style={{
            left: `calc(50% + ${frac * width}px)`,
            width: width * (0.16 + intensity * 0.06),
            height: width * (0.4 + intensity * 0.16),
            background: `linear-gradient(to top, ${color}, transparent)`,
            mixBlendMode: 'screen',
          }}
          animate={{ y: [0, -width * (0.55 + intensity * 0.2), -width * (1.05 + intensity * 0.35)], opacity: [0, peakOpacity, 0], scaleX: [1, 0.55, 0.2] }}
          transition={{ repeat: Infinity, duration: 2.4 + i * 0.25, delay: i * 0.35, ease: 'easeOut' }}
        />
      ))}
    </>
  );
};

interface PodiumTheme {
  tier: 'gold' | 'iron' | 'bronze';
  shieldWidth: number;
  shieldHeight: number;
  avatarSize: number;
  avatarTop: number;
  avatarFrame: string;
  auraColor: string;
  auraColorSoft: string;
  auraIntensity: number;
  raySize: number;
  rayColor: string;
  numberBg: string;
  numberText: string;
  nameSize: string;
  valueSize: string;
  valueColor: string;
  valueGlow: string;
  pedestalHeight: string;
  pedestalGradient: string;
  pedestalBorder: string;
  wrapperWidth: string;
  z: string;
}

const PODIUM_THEME: Record<1 | 2 | 3, PodiumTheme> = {
  1: {
    tier: 'gold',
    shieldWidth: 220, shieldHeight: 264,
    avatarSize: 118, avatarTop: 40,
    avatarFrame: 'bg-gradient-to-b from-yellow-200 via-yellow-400 to-yellow-700',
    auraColor: 'rgba(250,204,21,0.65)',
    auraColorSoft: 'rgba(255,247,200,0.55)',
    auraIntensity: 1,
    raySize: 190,
    rayColor: 'text-yellow-200',
    numberBg: 'bg-yellow-400',
    numberText: 'text-yellow-950',
    nameSize: 'text-4xl md:text-5xl',
    valueSize: 'text-5xl md:text-6xl',
    valueColor: 'text-yellow-300',
    valueGlow: 'drop-shadow-[0_0_20px_rgba(250,204,21,0.7)]',
    pedestalHeight: 'h-72',
    pedestalGradient: 'bg-gradient-to-t from-yellow-300 via-yellow-500 to-yellow-600',
    pedestalBorder: 'border-t-2 border-l-2 border-r-2 border-yellow-200/60 shadow-[0_-15px_50px_rgba(234,179,8,0.35)]',
    wrapperWidth: 'w-[42%]',
    z: 'z-20',
  },
  2: {
    tier: 'iron',
    shieldWidth: 176, shieldHeight: 211,
    avatarSize: 90, avatarTop: 32,
    avatarFrame: 'bg-gradient-to-b from-slate-500 via-slate-700 to-slate-950',
    auraColor: 'rgba(148,163,184,0.75)',
    auraColorSoft: 'rgba(226,232,240,0.4)',
    auraIntensity: 0.65,
    raySize: 140,
    rayColor: 'text-slate-500',
    numberBg: 'bg-slate-400',
    numberText: 'text-slate-950',
    nameSize: 'text-2xl md:text-3xl',
    valueSize: 'text-3xl md:text-4xl',
    valueColor: 'text-slate-300',
    valueGlow: 'drop-shadow-[0_0_10px_rgba(100,116,139,0.5)]',
    pedestalHeight: 'h-56',
    pedestalGradient: 'bg-gradient-to-t from-slate-700 via-slate-800 to-slate-950',
    pedestalBorder: 'border-t border-l border-r border-white/10',
    wrapperWidth: 'w-[30%]',
    z: 'z-10',
  },
  3: {
    tier: 'bronze',
    shieldWidth: 160, shieldHeight: 192,
    avatarSize: 82, avatarTop: 28,
    avatarFrame: 'bg-gradient-to-b from-amber-300 via-amber-600 to-amber-900',
    auraColor: 'rgba(217,119,6,0.8)',
    auraColorSoft: 'rgba(253,230,138,0.45)',
    auraIntensity: 0.45,
    raySize: 120,
    rayColor: 'text-amber-500',
    numberBg: 'bg-amber-600',
    numberText: 'text-amber-50',
    nameSize: 'text-xl md:text-2xl',
    valueSize: 'text-2xl md:text-3xl',
    valueColor: 'text-amber-400',
    valueGlow: 'drop-shadow-[0_0_10px_rgba(217,119,6,0.5)]',
    pedestalHeight: 'h-44',
    pedestalGradient: 'bg-gradient-to-t from-amber-600 via-amber-700 to-amber-900',
    pedestalBorder: 'border-t border-l border-r border-white/10',
    wrapperWidth: 'w-[30%]',
    z: 'z-10',
  },
};

const dayKey = (d: Date) => format(d, 'yyyy-MM-dd');

// Vendas lançadas antes de existir user_id na planilha ficaram gravadas com o
// vendedor como apelido em notes ("Vendedor: BIA"), sem vínculo com o cadastro
// do usuário. Mapeia os apelidos confirmados para o nome exato do cadastro.
const LEGACY_SELLER_ALIASES: Record<string, string> = {
  BIA: 'Beatriz Seabra',
  GUI: 'Guilherme',
  TASSIO: 'Tassio',
  RAFA: 'Raphael Da Paz',
};

const extractLegacySellerTag = (notes?: string): string | null => {
  const match = notes?.match(/vendedor:\s*([a-zà-ú]+)/i);
  return match ? match[1].toUpperCase() : null;
};

// Apelidos antigos só são confiáveis pra venda recente: nomes como RODRIGO/GEO
// aparecem só em vendas de jan-mar/2026, provavelmente de gente que já saiu do
// time, e não devem ser atribuídos a ninguém do cadastro atual.
const LEGACY_ALIAS_CUTOFF_MONTHS = 3;

// Um user_id real sempre vence a nota antiga: só recorre ao apelido em notes
// quando a venda não tem (ou não tem mais) um vendedor de verdade vinculado.
const resolveSaleOwnerId = (sale: Pick<Sale, 'user_id' | 'notes' | 'sale_date' | 'created_at'>, users: User[]): string | undefined => {
  if (sale.user_id && users.some(u => u.id === sale.user_id)) return sale.user_id;
  const tag = extractLegacySellerTag(sale.notes);
  const aliasName = tag ? LEGACY_SELLER_ALIASES[tag] : undefined;
  if (!aliasName) return undefined;
  const saleDate = new Date(sale.sale_date || sale.created_at || 0);
  if (saleDate < subMonths(new Date(), LEGACY_ALIAS_CUTOFF_MONTHS)) return undefined;
  return users.find(u => u.name.trim().toLowerCase() === aliasName.trim().toLowerCase())?.id;
};

const resolveSellerDisplayName = (sale: Pick<Sale, 'user_id' | 'notes' | 'sale_date' | 'created_at'>, users: User[]): string => {
  const ownerId = resolveSaleOwnerId(sale, users);
  const owner = ownerId ? users.find(u => u.id === ownerId) : undefined;
  if (owner) return owner.name;
  return extractLegacySellerTag(sale.notes) || 'Vendedor';
};

// Conta dias consecutivos com venda, terminando hoje (0 se hoje não vendeu)
const computeStreakDays = (salesByDay: Record<string, number>, today: Date): number => {
  let streak = 0;
  let cursor = today;
  while (salesByDay[dayKey(cursor)] > 0) {
    streak += 1;
    cursor = new Date(cursor.getTime() - 24 * 60 * 60 * 1000);
  }
  return streak;
};

export const GamifiedLeaderboard: React.FC = () => {
  const { company } = useAuth();
  const companyId = company?.id;
  
  const [sales, setSales] = useState<Sale[]>([]);
  // Bloqueado continua no ranking: bloquear corta acesso e novos leads, não apaga as vendas
  const { users } = useUsersData(companyId, undefined, { includeBlocked: true });
  // A assinatura realtime é criada uma vez (efeito não depende de `users`), então
  // o callback usa essa ref pra sempre ver a lista mais recente de vendedores em
  // vez de ficar preso no fechamento antigo (que podia estar vazio no mount).
  const usersRef = useRef<User[]>(users);
  useEffect(() => { usersRef.current = users; }, [users]);
  const { goals } = useGoalsData(companyId);
  const [loading, setLoading] = useState(true);
  const [filterMonth, setFilterMonth] = useState(format(new Date(), 'yyyy-MM'));
  const [muted, setMuted] = useState(() => {
    try { return localStorage.getItem('leaderboard_muted') === '1'; } catch { return false; }
  });
  const [celebration, setCelebration] = useState<{ name: string; value: number } | null>(null);
  const previousRanksRef = useRef<Record<string, number>>({});
  const [historicalSales, setHistoricalSales] = useState<Pick<Sale, 'user_id' | 'product_name' | 'notes' | 'sale_date' | 'created_at'>[]>([]);

  const toggleMute = () => {
    setMuted(prev => {
      const next = !prev;
      try { localStorage.setItem('leaderboard_muted', next ? '1' : '0'); } catch {}
      return next;
    });
  };

  useEffect(() => {
    if (!companyId) return;

    // O nível compara a contagem de vendas de cada mês com a do mês anterior,
    // então busca o histórico completo da empresa uma vez, independente do
    // mês filtrado na tela — de todo mundo, não só as próprias vendas, por
    // isso via get_sales_leaderboard (mesma função do ranking) em vez de ler
    // a tabela sales direto.
    supabase
      .rpc('get_sales_leaderboard', {
        p_company_id: companyId,
        p_start: '1970-01-01T00:00:00.000Z',
        p_end: new Date().toISOString(),
      })
      .then(({ data, error }) => {
        if (!error && data) setHistoricalSales(data as any[]);
      });
  }, [companyId]);

  useEffect(() => {
    if (!companyId) return;

    const fetchSales = async () => {
      setLoading(true);
      const targetDate = new Date(filterMonth + '-01T12:00:00');
      const start = startOfMonth(targetDate).toISOString();
      const end = endOfMonth(targetDate).toISOString();

      // O ranking mostra todos os vendedores pra qualquer usuário, mas a tabela sales só
      // entrega as vendas do próprio usuário a quem não é gerência. A função devolve só o
      // que o ranking e a trilha de níveis usam, sem dados de cliente (nome/telefone/e-mail).
      const { data, error } = await supabase.rpc('get_sales_leaderboard', {
        p_company_id: companyId,
        p_start: start,
        p_end: end,
      });

      if (!error && data) {
        setSales(data as Sale[]);
      }
      setLoading(false);
    };

    fetchSales();

    // Setup Realtime Subscription
    const channel = supabase.channel('schema-db-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'sales',
          filter: `company_id=eq.${companyId}`,
        },
        (payload) => {
          const newSale = payload.new as Sale;
          const saleValue = newSale.value || newSale.total_value || 0;

          // Trigger Confetti
          triggerConfetti(saleValue);

          // Banner de comemoração em tela cheia para vendas grandes
          if (saleValue >= BIG_SALE_THRESHOLD) {
            const sellerName = resolveSellerDisplayName(newSale, usersRef.current);
            setCelebration({ name: sellerName, value: saleValue });
            setTimeout(() => setCelebration(null), 5000);
          }

          // Add to state
          setSales((prev) => [...prev, newSale]);
          setHistoricalSales((prev) => [...prev, { user_id: newSale.user_id, product_name: newSale.product_name, notes: newSale.notes, sale_date: newSale.sale_date, created_at: newSale.created_at }]);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [companyId, filterMonth]);

  const triggerConfetti = (saleValue: number = 0) => {
    // Play celebration sound
    if (!muted) {
      try {
        let soundUrl = 'https://assets.mixkit.co/active_storage/sfx/2285/2285-preview.mp3'; // Orchestra triumphant trumpets — venda pequena
        let volume = 1.0;

        if (saleValue >= 15000) {
            soundUrl = 'https://assets.mixkit.co/active_storage/sfx/226/226-preview.mp3'; // Medieval show fanfare announcement — venda grande
        } else if (saleValue >= 5000) {
            soundUrl = 'https://assets.mixkit.co/active_storage/sfx/2293/2293-preview.mp3'; // Trumpet fanfare — venda média
        }

        const audio = new Audio(soundUrl);
        audio.volume = volume;
        audio.play().then(() => {
            setTimeout(() => {
                let currentVol = volume;
                const fadeOut = setInterval(() => {
                    currentVol -= 0.05;
                    if (currentVol <= 0) {
                        clearInterval(fadeOut);
                        audio.pause();
                        audio.currentTime = 0;
                    } else {
                        audio.volume = Math.max(0, currentVol);
                    }
                }, 50);
            }, 9000);
        }).catch(e => console.log('Audio play error:', e));
      } catch (error) {
        console.log('Error with audio:', error);
      }
    }

    const duration = 3000;
    const end = Date.now() + duration;

    const frame = () => {
      confetti({
        particleCount: 5,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        colors: ['#c29a67', '#f0e6d2', '#ffffff']
      });
      confetti({
        particleCount: 5,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        colors: ['#c29a67', '#f0e6d2', '#ffffff']
      });

      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    };
    frame();
  };

  const triggerGoalSound = () => {
    if (muted) return;
    try {
      const audio = new Audio('https://assets.mixkit.co/active_storage/sfx/694/694-preview.mp3'); // Medieval orchestra announcement — meta batida
      audio.volume = 1.0;
      audio.play().then(() => {
          setTimeout(() => {
              let currentVol = 1.0;
              const fadeOut = setInterval(() => {
                  currentVol -= 0.05;
                  if (currentVol <= 0) {
                      clearInterval(fadeOut);
                      audio.pause();
                      audio.currentTime = 0;
                  } else {
                      audio.volume = Math.max(0, currentVol);
                  }
              }, 50);
          }, 9000);
      }).catch(e => console.log('Audio play error:', e));
    } catch (error) {
      console.log('Error with goal audio:', error);
    }
  };

  const prevSalesLength = useRef(0);
  const prevCompletedGoals = useRef<Set<string>>(new Set());

  const isSaleForGoal = (sale: Sale, goal: { type: string; assigned_to?: string }): boolean => {
    if (goal.type === 'team') return true;
    return resolveSaleOwnerId(sale, users) === goal.assigned_to;
  };

  useEffect(() => {
     if (!goals || goals.length === 0 || sales.length === 0) return;
     
     const currentCompleted = new Set<string>();
     goals.forEach(goal => {
         const salesForThisGoal = sales.filter(s =>
             dentroDaMeta(s.sale_date, goal) &&
             (s.product_name?.toLowerCase().includes('acm') || s.product_name?.toLowerCase().includes('mentoria') || s.product_name?.toLowerCase().includes('método') || s.product_name?.toLowerCase().includes('metodo')) &&
             isSaleForGoal(s, goal)
         );
         let progress = 0;
         if (goal.metric_type === 'doors') {
             progress = salesForThisGoal.length;
         } else {
             progress = salesForThisGoal.reduce((sum, sale) => sum + (sale.value || sale.total_value || 0), 0);
         }
         if (goal.target_value > 0 && progress >= goal.target_value) {
             currentCompleted.add(goal.id);
         }
     });

     if (prevSalesLength.current > 0 && sales.length > prevSalesLength.current) {
         let newGoalBeaten = false;
         currentCompleted.forEach(id => {
             if (!prevCompletedGoals.current.has(id)) newGoalBeaten = true;
         });
         
         if (newGoalBeaten) {
             // Play goal sound after 10s (waiting for the sale sound to finish)
             setTimeout(() => {
                 triggerGoalSound();
             }, 10000);
         }
     }

     prevSalesLength.current = sales.length;
     prevCompletedGoals.current = currentCompleted;
  }, [sales, goals]);

  const isCurrentMonth = filterMonth === format(new Date(), 'yyyy-MM');

  // Contagem de vendas ACM por vendedor, quebrada por mês — é a base pro
  // "bate o mês anterior pra subir, empata pra manter, cai vendendo menos".
  const monthlyAcmCountsByUser = useMemo(() => {
    const counts: Record<string, Record<string, number>> = {};
    historicalSales.forEach(s => {
      if (!ehVendaAcm(s.product_name)) return;
      const ownerId = resolveSaleOwnerId(s, users);
      if (!ownerId) return;
      const month = monthKey(new Date(s.sale_date || s.created_at || 0));
      counts[ownerId] = counts[ownerId] || {};
      counts[ownerId][month] = (counts[ownerId][month] || 0) + 1;
    });
    return counts;
  }, [historicalSales, users]);

  // O nível é sempre calculado até o mês em tela (inclusive o mês corrente,
  // ainda em andamento): subir/manter/cair acontece na hora, assim que a
  // contagem do mês cruza a do mês anterior — não espera o mês fechar.
  const levelStatusByUser = useMemo(() => {
    const statuses: Record<string, { levelIndex: number; levelBarToBeat: number }> = {};
    users.forEach(user => {
      const monthlyCounts = monthlyAcmCountsByUser[user.id] || {};
      const { levelIndex, priorCount } = computeLevelStatus(monthlyCounts, filterMonth);
      statuses[user.id] = { levelIndex, levelBarToBeat: priorCount };
    });
    return statuses;
  }, [monthlyAcmCountsByUser, users, filterMonth]);

  const rankedUsers = useMemo(() => {
    if (!users || users.length === 0) return [];

    const today = new Date();

    const userStats = users.map(user => {
      const userSales = sales.filter(s => {
        if (resolveSaleOwnerId(s, users) !== user.id) return false;
        const productName = (s.product_name || '').toLowerCase();
        return productName.includes('acm') || productName.includes('mentoria') || productName.includes('método') || productName.includes('metodo');
      });

      let monthlyRevenue = 0;
      let monthlySalesCount = 0;
      let dailyRevenue = 0;
      let dailySalesCount = 0;
      let maxSingleSale = 0;
      const salesByDay: Record<string, number> = {};

      userSales.forEach(s => {
        const val = s.total_value || s.value || 0;
        monthlyRevenue += val;
        monthlySalesCount += 1;
        maxSingleSale = Math.max(maxSingleSale, val);

        const saleDate = new Date(s.sale_date || s.created_at || new Date());
        salesByDay[dayKey(saleDate)] = (salesByDay[dayKey(saleDate)] || 0) + val;
        if (isSameDay(saleDate, today)) {
          dailyRevenue += val;
          dailySalesCount += 1;
        }
      });

      const streak = computeStreakDays(salesByDay, today);
      const sparkline = Array.from({ length: 7 }).map((_, i) => {
        const d = new Date(today.getTime() - (6 - i) * 24 * 60 * 60 * 1000);
        return salesByDay[dayKey(d)] || 0;
      });

      return {
        ...user,
        monthlySales: monthlySalesCount,
        monthlyRevenue,
        dailySales: dailySalesCount,
        dailyRevenue,
        maxSingleSale,
        streak,
        sparkline,
        levelIndex: levelStatusByUser[user.id]?.levelIndex ?? 0,
        levelBarToBeat: levelStatusByUser[user.id]?.levelBarToBeat ?? 0,
      } as LeaderboardUser;
    });

    const activeUsers = userStats.filter(u => u.monthlyRevenue > 0);
    const sorted = activeUsers.sort((a, b) => b.monthlyRevenue - a.monthlyRevenue);

    return sorted.map((u, index) => ({
      ...u,
      rank: index + 1,
      previousRank: previousRanksRef.current[u.id],
    }));
  }, [sales, users, levelStatusByUser]);

  // Guarda o rank desta renderização para comparar na próxima mudança de dados
  useEffect(() => {
    const map: Record<string, number> = {};
    rankedUsers.forEach(u => { map[u.id] = u.rank; });
    previousRanksRef.current = map;
  }, [rankedUsers]);

  const badges = useMemo<LeaderboardBadges>(() => {
    if (rankedUsers.length === 0) return {};
    const result: LeaderboardBadges = {};

    const dailyLeader = rankedUsers.filter(u => u.dailyRevenue > 0).sort((a, b) => b.dailyRevenue - a.dailyRevenue)[0];
    if (dailyLeader) result.dailyLeaderId = dailyLeader.id;

    const topSale = [...rankedUsers].sort((a, b) => b.maxSingleSale - a.maxSingleSale)[0];
    if (topSale && topSale.maxSingleSale > 0) result.topSaleId = topSale.id;

    const climbers = rankedUsers
      .filter(u => u.previousRank !== undefined && u.previousRank > u.rank)
      .sort((a, b) => (b.previousRank! - b.rank) - (a.previousRank! - a.rank));
    if (climbers[0]) result.comebackId = climbers[0].id;

    if (isCurrentMonth) {
      const elapsedDays = new Date().getDate();
      const mostConsistent = rankedUsers.find(u => elapsedDays >= 3 && u.streak >= elapsedDays);
      if (mostConsistent) result.consistentId = mostConsistent.id;
    }

    return result;
  }, [rankedUsers, isCurrentMonth]);

  const tickerItems = useMemo(() => {
    return [...sales]
      .sort((a, b) => new Date(b.sale_date || b.created_at || 0).getTime() - new Date(a.sale_date || a.created_at || 0).getTime())
      .slice(0, 15)
      .map(s => ({
        name: resolveSellerDisplayName(s, users),
        value: s.total_value || s.value || 0,
        product: s.product_name || '',
      }));
  }, [sales, users]);

  if (loading) {
    return (
      <div className="h-screen w-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-[#c29a67] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const top3 = rankedUsers.slice(0, 3);
  const others = rankedUsers.slice(3);

  const fmt = (val: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);

  const RankChange: React.FC<{ user: LeaderboardUser }> = ({ user }) => {
    if (user.previousRank === undefined || user.previousRank === user.rank) return null;
    const improved = user.previousRank > user.rank;
    return (
      <span className={`inline-flex items-center gap-0.5 text-xs font-bold ${improved ? 'text-green-400' : 'text-red-400'}`}>
        {improved ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
        {Math.abs(user.previousRank - user.rank)}
      </span>
    );
  };

  const BadgeChips: React.FC<{ userId: string }> = ({ userId }) => {
    const chips: React.ReactNode[] = [];
    if (badges.dailyLeaderId === userId) {
      chips.push(<span key="daily" title="Fechador do dia" className="inline-flex items-center gap-1 bg-orange-500/20 text-orange-300 text-[11px] font-bold px-2 py-0.5 rounded-full border border-orange-500/40"><Zap size={10} /> Fechador do dia</span>);
    }
    if (badges.topSaleId === userId) {
      chips.push(<span key="topsale" title="Maior venda do mês" className="inline-flex items-center gap-1 bg-purple-500/20 text-purple-300 text-[11px] font-bold px-2 py-0.5 rounded-full border border-purple-500/40"><Award size={10} /> Maior venda</span>);
    }
    if (badges.comebackId === userId) {
      chips.push(<span key="comeback" title="Maior salto no ranking" className="inline-flex items-center gap-1 bg-green-500/20 text-green-300 text-[11px] font-bold px-2 py-0.5 rounded-full border border-green-500/40"><TrendingUp size={10} /> Comeback</span>);
    }
    if (badges.consistentId === userId) {
      chips.push(<span key="consistent" title="Vendeu todos os dias do mês até agora" className="inline-flex items-center gap-1 bg-blue-500/20 text-blue-300 text-[11px] font-bold px-2 py-0.5 rounded-full border border-blue-500/40"><CheckCircle2 size={10} /> Consistência</span>);
    }
    if (chips.length === 0) return null;
    return <div className="flex flex-wrap gap-1.5 mt-1.5">{chips}</div>;
  };

  const Sparkline: React.FC<{ data: number[] }> = ({ data }) => {
    const max = Math.max(...data, 1);
    const points = data.map((v, i) => `${(i / (data.length - 1)) * 60},${24 - (v / max) * 22 - 1}`).join(' ');
    return (
      <svg width="60" height="24" className="opacity-70">
        <polyline points={points} fill="none" stroke="#c29a67" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  };

  const LevelProgress: React.FC<{ levelIndex: number; monthlySales: number; barToBeat: number; live: boolean }> = ({ levelIndex, monthlySales, barToBeat, live }) => {
    const level = ACM_LEVELS[levelIndex];
    const hasHistory = barToBeat > 0 || monthlySales > 0;
    const trend: 'up' | 'same' | 'down' | null = hasHistory ? (monthlySales > barToBeat ? 'up' : monthlySales === barToBeat ? 'same' : 'down') : null;
    const trendColor = trend === 'up' ? 'text-green-400' : trend === 'down' ? 'text-red-400' : 'text-gray-500';
    // O nível já reflete a contagem do mês em tempo real (não é uma previsão
    // de fim de mês) — o texto só descreve por que ele está nesse nível agora.
    const trendLabel = trend === 'up'
      ? 'subiu de nível'
      : trend === 'same'
        ? 'mantém o nível'
        : trend === 'down'
          ? (live ? 'abaixo do mês passado, ainda dá tempo' : 'caiu de nível')
          : null;

    return (
      <div
        className="flex items-center gap-1.5 mt-1.5 flex-wrap"
        title={hasHistory ? `${monthlySales} vendas ${live ? 'neste mês (até agora)' : 'nesse mês'} vs ${barToBeat} no mês anterior · comissão ${level.commission.toFixed(1)}%` : `comissão ${level.commission.toFixed(1)}%`}
      >
        <span className="text-sm shrink-0">{level.icon}</span>
        <span className="text-[11px] font-bold uppercase tracking-wide text-gray-300 shrink-0">{level.name}</span>
        <span className="text-[11px] text-green-400 font-bold shrink-0">{level.commission.toFixed(1)}%</span>
        {trend && (
          <span className={`text-[11px] font-bold shrink-0 ${trendColor}`}>
            {monthlySales}/{barToBeat} · {trendLabel}
          </span>
        )}
      </div>
    );
  };

  const PodiumSlot: React.FC<{ user: LeaderboardUser; rank: 1 | 2 | 3; delay: number }> = ({ user, rank, delay }) => {
    const theme = PODIUM_THEME[rank];
    const isFirst = rank === 1;

    return (
      <motion.div
        initial={{ opacity: 0, y: isFirst ? 100 : 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={isFirst ? { duration: 0.6, type: 'spring', bounce: 0.4 } : { duration: 0.5, delay }}
        className={`flex flex-col items-center ${theme.z} ${theme.wrapperWidth} relative`}
      >
        <div className="relative mb-8" style={{ width: theme.shieldWidth, height: theme.shieldHeight }}>
          {/* Aura épica tipo energia/ki: blobs orgânicos pulsando atrás + uma
              camada extra em "screen" por cima do escudo, pra brilhar mesmo
              onde o centro do gradiente ficaria escondido atrás dele. */}
          <motion.div
            animate={{
              opacity: [0.7 * theme.auraIntensity + 0.15, 1, 0.7 * theme.auraIntensity + 0.15],
              scale: [1, 1.2, 1],
              rotate: [0, 12, 0],
            }}
            transition={{ repeat: Infinity, duration: 1.8 }}
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 blur-2xl pointer-events-none"
            style={{
              width: theme.raySize * (1.6 + theme.auraIntensity * 0.6),
              height: theme.raySize * (1.6 + theme.auraIntensity * 0.6),
              background: `radial-gradient(circle, ${theme.auraColor} 0%, ${theme.auraColor} 35%, transparent 75%)`,
              borderRadius: '42% 58% 63% 37% / 47% 41% 59% 53%',
            }}
          />
          <motion.div
            animate={{
              opacity: [0.3 * theme.auraIntensity + 0.1, 0.7 * theme.auraIntensity + 0.1, 0.3 * theme.auraIntensity + 0.1],
              scale: [1, 1.3, 1],
              rotate: [0, -10, 0],
            }}
            transition={{ repeat: Infinity, duration: 2.6, delay: 0.3 }}
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 blur-2xl pointer-events-none"
            style={{
              width: theme.raySize * (2.3 + theme.auraIntensity * 0.8),
              height: theme.raySize * (2.3 + theme.auraIntensity * 0.8),
              background: `radial-gradient(circle, ${theme.auraColorSoft} 0%, transparent 72%)`,
              borderRadius: '58% 42% 37% 63% / 53% 59% 41% 47%',
            }}
          />
          {/* Camada de brilho em cima do escudo (screen = só clareia, não tampa) */}
          <motion.div
            animate={{ opacity: [0.08 * theme.auraIntensity, 0.2 * theme.auraIntensity, 0.08 * theme.auraIntensity], scale: [1, 1.06, 1] }}
            transition={{ repeat: Infinity, duration: 1.6 }}
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 blur-2xl pointer-events-none z-[15]"
            style={{
              width: theme.shieldWidth * 1.1,
              height: theme.shieldHeight * 1.1,
              background: `radial-gradient(circle, ${theme.auraColor} 0%, transparent 65%)`,
              mixBlendMode: 'screen',
            }}
          />
          <KiWisps color={theme.auraColor} width={theme.shieldWidth} intensity={theme.auraIntensity} />
          {/* Raios girando atrás do escudo */}
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ repeat: Infinity, duration: isFirst ? 12 : 18, ease: 'linear' }}
            className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 ${theme.rayColor} pointer-events-none z-[16]`}
            style={{ opacity: 0.3 + theme.auraIntensity * 0.35, mixBlendMode: 'screen' }}
          >
            <Star size={theme.raySize} strokeWidth={0.5} className="fill-current" />
          </motion.div>

          {isFirst && (
            <motion.div
              animate={{ y: [0, -6, 0] }}
              transition={{ repeat: Infinity, duration: 2 }}
              className="absolute -top-11 left-1/2 -translate-x-1/2 z-30 text-yellow-300 drop-shadow-[0_0_15px_rgba(250,204,21,0.8)]"
            >
              <Crown size={44} className="fill-yellow-400" />
            </motion.div>
          )}
          {!isFirst && (
            <Sparkles size={rank === 2 ? 22 : 18} className={`absolute -top-3 ${rank === 2 ? '-left-3' : '-right-3'} z-30 ${theme.rayColor} opacity-80`} />
          )}

          {user.dailySales > 0 && (
            <motion.div
              animate={{ scale: [1, 1.2, 1], rotate: [0, 10, -10, 0] }}
              transition={{ repeat: Infinity, duration: 2 }}
              className="absolute -top-2 -right-2 bg-orange-600 text-white p-1.5 rounded-full shadow-[0_0_15px_rgba(249,115,22,0.6)] z-30"
            >
              <Flame size={isFirst ? 22 : 18} />
            </motion.div>
          )}

          {/* Arte do escudo de fantasia (dourado / ferro / bronze) */}
          <div className="absolute inset-0 z-10">
            <ShieldArt tier={theme.tier} />
          </div>

          {/* Foto do vendedor recortada no mesmo formato de escudo, montada como brasão */}
          <div
            className="absolute left-1/2 -translate-x-1/2 z-20"
            style={{ top: theme.avatarTop, width: theme.avatarSize, height: theme.avatarSize * 1.2 }}
          >
            <div className={`w-full h-full p-1 ${theme.avatarFrame} shadow-xl`} style={{ clipPath: SHIELD_CLIP }}>
              <img
                src={user.profile_picture || `https://ui-avatars.com/api/?name=${user.name}&background=random`}
                alt={user.name}
                className="w-full h-full object-cover"
                style={{ clipPath: SHIELD_CLIP }}
              />
            </div>
          </div>

          {/* Medalha com o número da colocação, na ponta do escudo */}
          <div className={`absolute -bottom-5 left-1/2 -translate-x-1/2 z-20 ${theme.numberBg} ${theme.numberText} font-black ${isFirst ? 'text-2xl w-12 h-12' : 'text-lg w-9 h-9'} rounded-full flex items-center justify-center border-4 border-[#0a0a0a] shadow-lg`}>
            {rank}
          </div>
        </div>

        <div className="flex items-center gap-2 mt-3">
          <h3 className={`${theme.nameSize} font-black text-white text-center uppercase tracking-wide truncate max-w-[280px]`}>{user.name}</h3>
          <RankChange user={user} />
        </div>
        <p className={`${theme.valueSize} font-black mt-1 ${theme.valueColor} ${theme.valueGlow}`}>{fmt(user.monthlyRevenue)}</p>

        <div className="flex items-center gap-2 flex-wrap justify-center mt-2">
          {user.dailySales > 0 && (
            <div className="flex items-center gap-2 bg-orange-500/20 px-3 py-1 rounded-full border border-orange-500/50">
              <Flame className="text-orange-400" size={14} />
              <span className="text-orange-200 font-bold text-xs">{user.dailySales} vendas hoje</span>
            </div>
          )}
          {user.streak > 1 && (
            <div className="flex items-center gap-2 bg-orange-500/10 px-3 py-1 rounded-full border border-orange-500/30">
              <Flame className="text-orange-400" size={14} />
              <span className="text-orange-300 font-bold text-xs">{user.streak} dias seguidos</span>
            </div>
          )}
        </div>

        <div className="w-full px-4 flex justify-center"><LevelProgress levelIndex={user.levelIndex} monthlySales={user.monthlySales} barToBeat={user.levelBarToBeat} live={isCurrentMonth} /></div>
        <BadgeChips userId={user.id} />

        <div className={`mt-4 w-full rounded-t-[32px] opacity-90 ${theme.pedestalHeight} ${theme.pedestalGradient} ${theme.pedestalBorder} relative overflow-hidden`}>
          <motion.div
            animate={{ x: ['-100%', '200%'] }}
            transition={{ repeat: Infinity, duration: 3, ease: 'linear', delay }}
            className="absolute top-0 bottom-0 left-0 w-1/3 bg-gradient-to-r from-transparent via-white/25 to-transparent skew-x-[-20deg]"
          />
        </div>
      </motion.div>
    );
  };

  return (
    <div className="h-screen w-screen bg-[#0a0a0a] overflow-hidden text-white font-sans flex flex-col relative">
      <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-to-br from-[#c29a67]/10 to-transparent rounded-full blur-[100px] -translate-y-1/2 translate-x-1/3 pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-blue-900/10 to-transparent rounded-full blur-[80px] translate-y-1/3 -translate-x-1/3 pointer-events-none" />

      <AnimatePresence>
        {celebration && (
          <motion.div
            key="celebration"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/85 backdrop-blur-sm pointer-events-none"
          >
            <motion.div
              initial={{ scale: 0.5, y: 40 }}
              animate={{ scale: 1, y: 0 }}
              transition={{ type: 'spring', bounce: 0.5 }}
              className="text-center"
            >
              <Trophy size={80} className="text-yellow-400 mx-auto mb-4 drop-shadow-[0_0_30px_rgba(250,204,21,0.6)]" />
              <h2 className="text-6xl font-black text-white uppercase tracking-tight">{celebration.name}</h2>
              <p className="text-3xl font-bold text-yellow-400 mt-2">venda de {fmt(celebration.value)}!</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <header className="p-8 pb-4 flex items-center justify-between z-30 relative border-b border-[#c29a67]/20 bg-black/40 backdrop-blur-md">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-gradient-to-br from-[#c29a67] to-[#8c6b45] rounded-2xl shadow-[0_0_20px_rgba(194,154,103,0.4)]">
            <Trophy size={32} className="text-black" />
          </div>
          <div>
            <h1 className="text-4xl font-black tracking-tighter uppercase bg-clip-text text-transparent bg-gradient-to-r from-white to-[#c29a67]">
              Evolua Leaderboard
            </h1>
            <p className="text-[#c29a67] font-semibold tracking-widest text-sm uppercase">Ranking de Vendas</p>
          </div>
        </div>
        <div className="flex items-center gap-6">
          <button
            onClick={toggleMute}
            title={muted ? 'Ativar som' : 'Silenciar'}
            className="p-3 rounded-2xl bg-black/40 border border-[#c29a67]/30 text-[#c29a67] hover:bg-[#c29a67]/10 transition-colors"
          >
            {muted ? <VolumeX size={20} /> : <Volume2 size={20} />}
          </button>
          <div
            className="text-right relative group cursor-pointer"
            onClick={(e) => {
               const input = e.currentTarget.querySelector('input');
               if (input && 'showPicker' in input) {
                  try { (input as any).showPicker(); } catch (err) {}
               }
            }}
          >
            <p className="text-xs text-gray-500 uppercase tracking-widest font-bold mb-1 group-hover:text-gray-300 transition-colors">
              Mês Filtrado
            </p>
            <div className="flex items-center gap-2 justify-end text-white group-hover:text-[#c29a67] transition-colors">
              <p className="text-xl font-bold capitalize pointer-events-none">
                 {new Date(filterMonth + '-01T12:00:00').toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}
              </p>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                <rect width="18" height="18" x="3" y="4" rx="2" ry="2"/>
                <line x1="16" x2="16" y1="2" y2="6"/>
                <line x1="8" x2="8" y1="2" y2="6"/>
                <line x1="3" x2="21" y1="10" y2="10"/>
              </svg>
            </div>
            {/* Input nativo invisível */}
            <input 
               type="month" 
               value={filterMonth}
               onChange={(e) => setFilterMonth(e.target.value)}
               className="absolute inset-0 opacity-0 cursor-pointer z-50 w-full h-full"
               style={{ pointerEvents: 'none' }} // Lets the click pass through to the div, which calls showPicker
               title="Mudar Mês"
            />
          </div>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden p-8 gap-8 z-10 relative">
        <div className="w-[70%] flex flex-col items-center justify-end relative pb-8">
          <div className="flex items-end justify-center gap-4 w-full h-[78vh] relative">
            {top3[1] && <PodiumSlot user={top3[1]} rank={2} delay={0.2} />}
            {top3[0] && <PodiumSlot user={top3[0]} rank={1} delay={0} />}
            {top3[2] && <PodiumSlot user={top3[2]} rank={3} delay={0.4} />}
          </div>
        </div>

        <div className="w-[30%] flex flex-col gap-6 h-full">
          {/* Top part: Others */}
          <div className="flex-1 flex flex-col bg-[#12100e]/80 backdrop-blur-xl border border-[#c29a67]/20 rounded-[40px] p-6 shadow-2xl overflow-hidden min-h-[40%]">
            <h2 className="text-xl font-bold uppercase tracking-widest text-gray-500 mb-4 flex items-center gap-3 border-b border-gray-800 pb-3">
              Demais Posições
            </h2>
            <div className="flex-1 overflow-y-auto pr-2 space-y-3 custom-scrollbar">
            <AnimatePresence>
              {others.map((user, index) => {
                const above = rankedUsers[index + 3 - 1];
                const gapToAbove = above ? above.monthlyRevenue - user.monthlyRevenue : 0;
                const isLast = user.rank === rankedUsers.length && rankedUsers.length > 3;

                return (
                <motion.div
                  key={user.id}
                  layout
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  className={`bg-black/50 border ${isLast ? 'border-red-500/40' : 'border-white/5'} rounded-3xl p-5 flex items-center justify-between hover:bg-white/5 transition-colors group relative overflow-hidden`}
                >
                  {user.dailySales > 0 && (
                    <motion.div
                      animate={{ opacity: [0, 0.2, 0] }}
                      transition={{ repeat: Infinity, duration: 2 }}
                      className="absolute inset-0 bg-gradient-to-r from-orange-500/0 via-orange-500/20 to-orange-500/0 -translate-x-full group-hover:animate-[shimmer_2s_infinite]"
                    />
                  )}

                  <div className="flex items-center gap-5 relative z-10">
                    <div className="w-10 text-center text-gray-500 font-black text-2xl">
                      {user.rank}º
                    </div>
                    <div className="relative">
                      <img src={user.profile_picture || `https://ui-avatars.com/api/?name=${user.name}&background=random`} alt={user.name} className="w-14 h-14 rounded-full object-cover border-2 border-gray-700 group-hover:border-[#c29a67] transition-colors" />
                      {user.dailySales > 0 && (
                         <div className="absolute -bottom-1 -right-1 bg-orange-500 p-1 rounded-full border-2 border-black">
                           <Flame size={14} className="text-white" />
                         </div>
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-bold text-white text-xl">{user.name}</h4>
                        <RankChange user={user} />
                        {isLast && <span className="text-[11px] font-bold uppercase text-red-400 bg-red-500/10 border border-red-500/30 px-2 py-0.5 rounded-full">Zona de risco</span>}
                      </div>
                      <p className="text-sm text-gray-500">
                        {user.monthlySales} venda(s) no mês
                        {user.streak > 1 && <span className="text-orange-400 font-bold ml-2">🔥 {user.streak} dias seguidos</span>}
                      </p>
                      {gapToAbove > 0 && (
                        <p className="text-xs text-gray-500 mt-0.5">Faltam <span className="text-[#c29a67] font-bold">{fmt(gapToAbove)}</span> para ultrapassar {above?.name}</p>
                      )}
                      <LevelProgress levelIndex={user.levelIndex} monthlySales={user.monthlySales} barToBeat={user.levelBarToBeat} live={isCurrentMonth} />
                      <BadgeChips userId={user.id} />
                    </div>
                  </div>
                  <div className="text-right relative z-10 flex flex-col items-end gap-1">
                    <Sparkline data={user.sparkline} />
                    <p className="font-black text-2xl text-[#c29a67]">{fmt(user.monthlyRevenue)}</p>
                    {user.dailySales > 0 && (
                      <p className="text-sm text-orange-400 font-bold">+{fmt(user.dailyRevenue)} hoje!</p>
                    )}
                  </div>
                </motion.div>
                );
              })}
              {others.length === 0 && (
                <div className="text-center text-gray-500 mt-10">
                  Nenhum outro vendedor pontuou neste mês.
                </div>
              )}
            </AnimatePresence>
            </div>
          </div>
          
          {/* Bottom part: Goals */}
          <div className="h-[45%] flex flex-col bg-[#12100e]/80 backdrop-blur-xl border border-[#c29a67]/20 rounded-[40px] p-6 shadow-2xl overflow-hidden">
            <h2 className="text-xl font-bold uppercase tracking-widest text-gray-500 mb-4 flex items-center gap-3 border-b border-gray-800 pb-3">
              Metas do Período
            </h2>
            <div className="flex-1 overflow-y-auto pr-2 space-y-3 custom-scrollbar">
              <AnimatePresence>
                {goals && goals.length > 0 ? (
                  goals.map((goal, index) => {
                    const salesForThisGoal = sales.filter(s =>
                      dentroDaMeta(s.sale_date, goal) &&
                      (s.product_name?.toLowerCase().includes('acm') || s.product_name?.toLowerCase().includes('mentoria') || s.product_name?.toLowerCase().includes('método') || s.product_name?.toLowerCase().includes('metodo')) &&
                      isSaleForGoal(s, goal)
                    );

                    let progress = 0;
                    if (goal.metric_type === 'doors') {
                      progress = salesForThisGoal.length;
                    } else {
                      progress = salesForThisGoal.reduce((sum, sale) => sum + (sale.value || sale.total_value || 0), 0);
                    }

                    const percent = goal.target_value > 0 ? ((progress / goal.target_value) * 100) : 0;
                    const isCompleted = percent >= 100;
                    const isAlmost = percent >= 80 && percent < 100;

                    return (
                      <motion.div 
                        key={goal.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.4, delay: index * 0.1 }}
                        className={`bg-black/50 border ${isCompleted ? 'border-orange-500/80 shadow-[0_0_20px_rgba(249,115,22,0.4)]' : isAlmost ? 'border-orange-500/30' : 'border-white/5'} rounded-2xl p-4 flex flex-col gap-2 relative overflow-hidden group`}
                      >
                        {isCompleted && (
                          <>
                            <motion.div 
                              animate={{ opacity: [0.1, 0.3, 0.1], scale: [1, 1.05, 1] }}
                              transition={{ repeat: Infinity, duration: 1.5 }}
                              className="absolute inset-0 bg-gradient-to-r from-orange-600/20 via-red-500/20 to-orange-600/20 pointer-events-none"
                            />
                            {/* Animated flames in background */}
                            <motion.div 
                              animate={{ y: [0, -10, 0], opacity: [0.3, 0.6, 0.3] }}
                              transition={{ repeat: Infinity, duration: 2, delay: 0 }}
                              className="absolute -bottom-4 -left-4 text-orange-500/20 pointer-events-none rotate-45 scale-150"
                            >
                              <Flame size={60} />
                            </motion.div>
                            <motion.div 
                              animate={{ y: [0, -15, 0], opacity: [0.2, 0.5, 0.2] }}
                              transition={{ repeat: Infinity, duration: 1.5, delay: 0.5 }}
                              className="absolute -bottom-6 -right-4 text-red-500/20 pointer-events-none -rotate-12 scale-150"
                            >
                              <Flame size={80} />
                            </motion.div>
                          </>
                        )}
                        {isAlmost && !isCompleted && (
                          <motion.div 
                            animate={{ opacity: [0, 0.1, 0] }}
                            transition={{ repeat: Infinity, duration: 2 }}
                            className="absolute inset-0 bg-gradient-to-r from-orange-500/0 via-orange-500/10 to-orange-500/0 -translate-x-full group-hover:animate-[shimmer_2s_infinite]"
                          />
                        )}
                        
                        <div className="flex justify-between items-start z-10 relative">
                          <div className="flex items-start gap-3">
                            <div className={`p-2 rounded-xl mt-1 ${isCompleted ? 'bg-gradient-to-br from-orange-500 to-red-600 text-white shadow-lg shadow-orange-500/50' : isAlmost ? 'bg-orange-500/20 text-orange-400' : 'bg-[#c29a67]/20 text-[#c29a67]'}`}>
                                {goal.type === 'team' ? <Users size={18} /> : <Target size={18} />}
                            </div>
                            <div>
                                <span className="text-[11px] font-bold uppercase tracking-wider text-gray-500">
                                  {goal.type === 'team' ? 'Meta de Time' : 'Meta Individual'}
                                  {goal.type === 'individual' && goal.assigned_to && users.find(u => u.id === goal.assigned_to) ? ` - ${users.find(u => u.id === goal.assigned_to)?.name}` : ''}
                                </span>
                                <h4 className="font-bold text-white text-base leading-tight flex items-center gap-2">
                                  {goal.title}
                                  {isCompleted && (
                                    <motion.div 
                                      animate={{ scale: [1, 1.3, 1], rotate: [-10, 10, -10] }} 
                                      transition={{ repeat: Infinity, duration: 0.5 }}
                                    >
                                      <Flame size={20} className="text-orange-400 drop-shadow-[0_0_5px_rgba(249,115,22,0.8)] fill-orange-500" />
                                    </motion.div>
                                  )}
                                </h4>
                            </div>
                          </div>
                          <div className="text-right">
                            <span className={`text-xl font-black ${isCompleted ? 'text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-red-500 drop-shadow-[0_0_8px_rgba(249,115,22,0.6)]' : isAlmost ? 'text-orange-400' : 'text-[#c29a67]'}`}>
                              {percent.toFixed(0)}%
                            </span>
                          </div>
                        </div>
                        
                        <div className="text-xs text-gray-500 font-medium z-10 relative ml-[46px] -mt-2">
                          {goal.metric_type === 'doors' ? `${progress} / ${goal.target_value} portas` : `${fmt(progress)} / ${fmt(goal.target_value)}`}
                        </div>
                        
                        <div className="w-full bg-gray-800 rounded-full h-2 mt-1 overflow-hidden relative z-10">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${Math.min(percent, 100)}%` }}
                            transition={{ duration: 1.5, ease: "easeOut", type: "spring", bounce: 0.3 }}
                            className={`h-full rounded-full relative ${isCompleted ? 'bg-gradient-to-r from-orange-500 to-red-600 shadow-[0_0_15px_rgba(239,68,68,0.8)]' : isAlmost ? 'bg-gradient-to-r from-orange-400 to-orange-500' : 'bg-gradient-to-r from-[#8c6b45] to-[#c29a67]'}`}
                          >
                            <motion.div 
                                animate={{ x: ['-100%', '200%'] }} 
                                transition={{ repeat: Infinity, duration: isCompleted ? 1 : 2, ease: 'linear' }}
                                className="absolute top-0 bottom-0 left-0 w-1/2 bg-gradient-to-r from-transparent via-white/40 to-transparent skew-x-[-20deg]"
                            />
                          </motion.div>
                        </div>
                      </motion.div>
                    );
                  })
                ) : (
                  <div className="text-center text-gray-500 mt-6 text-sm">
                    Nenhuma meta cadastrada.
                  </div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>

      <div className="h-12 border-t border-[#c29a67]/20 bg-black/60 backdrop-blur-md overflow-hidden flex items-center relative z-10 shrink-0">
        {tickerItems.length > 0 ? (
          <div className="whitespace-nowrap flex gap-12 animate-[marquee_30s_linear_infinite] px-6">
            {[...tickerItems, ...tickerItems].map((item, i) => (
              <span key={i} className="text-sm text-gray-300 flex items-center gap-2 shrink-0">
                <Flame size={14} className="text-orange-500" />
                <strong className="text-white">{item.name}</strong> vendeu {fmt(item.value)}{item.product ? ` — ${item.product}` : ''}
              </span>
            ))}
          </div>
        ) : (
          <span className="text-sm text-gray-500 px-6">Aguardando novas vendas...</span>
        )}
      </div>

      <style dangerouslySetInnerHTML={{__html: `
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(0,0,0,0.2);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(194, 154, 103, 0.3);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(194, 154, 103, 0.6);
        }
        @keyframes shimmer {
          100% { transform: translateX(100%); }
        }
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}} />
    </div>
  );
};
